// src/client/game/core/Snake.ts
import type { Ability, Direction, GridPos } from "./types";

/**
 * Pure snake state + movement rules.
 * NOTE: No Phaser/DOM here.
 */
export class Snake {
  segments: GridPos[];
  direction: Direction;
  pendingDirection: Direction;

  // Buffer exactly ONE extra turn (so UP then LEFT works reliably).
  queuedDirection: Direction | null;

  baseSpeedMs: number;
  speedMultiplier: number;
  score: number;
  abilities: Ability[];

  /** Laser level (0 = locked). Max is enforced by food logic. */
  laserLevel: number;

  constructor(
    start: GridPos,
    initialLength: number,
    initialDirection: Direction,
    baseSpeedMs: number
  ) {
    this.direction = { ...initialDirection };
    this.pendingDirection = { ...initialDirection };
    this.queuedDirection = null;

    this.baseSpeedMs = baseSpeedMs;
    this.speedMultiplier = 1;
    this.score = 0;
    this.abilities = [];
    this.laserLevel = 0;

    // Head at index 0, tail at end.
    this.segments = [];
    for (let i = 0; i < initialLength; i++) {
      this.segments.push({
        x: start.x - i * initialDirection.dx,
        y: start.y - i * initialDirection.dy,
      });
    }
  }

  private isOpposite(a: Direction, b: Direction): boolean {
    return a.dx + b.dx === 0 && a.dy + b.dy === 0;
  }

  private isSame(a: Direction, b: Direction): boolean {
    return a.dx === b.dx && a.dy === b.dy;
  }

  /**
   * Direction input handling:
   * - You can’t reverse immediately.
   * - If you input two turns within one tick (e.g. UP then LEFT),
   *   we buffer the second input and apply it on the following move.
   */
  setDirection(next: Direction): void {
    // Ignore duplicates
    if (this.isSame(next, this.pendingDirection)) return;
    if (this.queuedDirection && this.isSame(next, this.queuedDirection)) return;

    const hasPendingTurn = !this.isSame(this.pendingDirection, this.direction);

    if (!hasPendingTurn) {
      // No turn scheduled yet -> schedule for next step
      if (this.isOpposite(this.direction, next)) return; // can't reverse immediately
      this.pendingDirection = { ...next };
      this.queuedDirection = null;
      return;
    }

    // Turn already scheduled for next step -> buffer ONE more for the step after
    if (this.isOpposite(this.pendingDirection, next)) return; // can't reverse relative to pending
    this.queuedDirection = { ...next };
  }

  get head(): GridPos {
    return this.segments[0];
  }

  /**
   * Move forward one cell with wrap-around.
   * Returns collision info.
   */
  step(
    gridWidth: number,
    gridHeight: number,
    obstacleSet: Set<string>
  ): {
    newHead: GridPos;
    removedTail: GridPos;
    hitSelf: boolean;
    hitObstacle: boolean;
  } {
    // Apply direction for THIS move
    this.direction = { ...this.pendingDirection };

    // Prepare next move direction:
    if (this.queuedDirection) {
      this.pendingDirection = { ...this.queuedDirection };
      this.queuedDirection = null;
    } else {
      this.pendingDirection = { ...this.direction };
    }

    const currentHead = this.head;
    let nx = currentHead.x + this.direction.dx;
    let ny = currentHead.y + this.direction.dy;

    // Wrap-around
    if (nx < 0) nx = gridWidth - 1;
    if (nx >= gridWidth) nx = 0;
    if (ny < 0) ny = gridHeight - 1;
    if (ny >= gridHeight) ny = 0;

    const newHead: GridPos = { x: nx, y: ny };

    // Self-collision (ignore cell where tail will move)
    const hitSelf = this.segments.some((seg, index) => {
      if (index === this.segments.length - 1) return false;
      return seg.x === newHead.x && seg.y === newHead.y;
    });

    const key = `${newHead.x},${newHead.y}`;
    const hitObstacle = obstacleSet.has(key);

    const removedTail = this.segments[this.segments.length - 1];
    this.segments.pop();
    this.segments.unshift(newHead);

    return { newHead, removedTail, hitSelf, hitObstacle };
  }

  grow(amount: number): void {
    if (amount <= 0) return;
    const tail = this.segments[this.segments.length - 1];
    for (let i = 0; i < amount; i++) {
      this.segments.push({ x: tail.x, y: tail.y });
    }
  }

  shrinkHalf(minLength = 3): void {
    const current = this.segments.length;
    const target = Math.max(minLength, Math.ceil(current / 2));
    if (target >= current) return;
    this.segments = this.segments.slice(0, target);
  }

  get length(): number {
    return this.segments.length;
  }

  getSpeedMs(): number {
    return this.baseSpeedMs / this.speedMultiplier;
  }
}
